class SelectionManager {
  constructor() {
    this.maxQty = 1;
    this.selectedPatterns = new Set();
    this.selectedBooks = new Set();
    this.PATTERN_WEIGHT = 1;
    this.BOOK_WEIGHT = 2;
    this.withoutDiscountKits = 0;
    this.variantPopup = {
      overlay: null,
      currentProductId: null,
      currentCheckbox: null,
      currentType: null
    };
    this.isMobile = this.checkIfMobile();
    this.init();
  }
  
  // Check if device is mobile or tablet
  checkIfMobile() {
    return window.innerWidth <= 1024;
  }
  
  init() {
    this.readMetaTagData();
    this.setupQuantityListener();
    this.setupCheckboxListeners();
    this.setupVariantPopup();
    this.observeMetaTag();
    
    // Listen for window resize to update mobile state
    window.addEventListener('resize', () => {
      this.isMobile = this.checkIfMobile();
    });
    
    const currentQty = parseInt(document.querySelector('.quantity__input')?.value || 1);
    this.updateMaxQty(currentQty);
  }
  
  readMetaTagData() {
    const metaTag = document.querySelector('meta[get-kits-data]');
    if (metaTag) {
      this.withoutDiscountKits = parseInt(metaTag.getAttribute('data-whithout-discount-kits') || 0);
    }
  }
  
  observeMetaTag() {
    const metaTag = document.querySelector('meta[get-kits-data]');
    if (metaTag) {
      const observer = new MutationObserver(() => {
        this.readMetaTagData();
        this.updateMaxQty(parseInt(document.querySelector('.quantity__input')?.value || 1));
      });
      observer.observe(metaTag, {
        attributes: true,
        attributeFilter: ['data-whithout-discount-kits']
      });
    }
  }
  
  setupQuantityListener() {
    const quantityInput = document.querySelector('.quantity__input');
    if (quantityInput) {
      quantityInput.addEventListener('change', (e) => {
        this.updateMaxQty(parseInt(e.target.value));
      });
    }
    document.querySelectorAll('.btn-quantity').forEach(btn => {
      btn.addEventListener('click', () => {
        setTimeout(() => this.updateMaxQty(
          parseInt(document.querySelector('.quantity__input').value)
        ), 50);
      });
    });
  }
  
  updateMaxQty(qty) {
    this.maxQty = qty + this.withoutDiscountKits;
    this.validateSelections();
    this.updateUI();
  }
  
  setupCheckboxListeners() {
    document.querySelectorAll('input[type="checkbox"][name="additonal-id"]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => this.handleCheckboxChange(e));
    });
  }
  
  setupVariantPopup() {
    // Create popup if it doesn't exist (for desktop)
    if (!document.getElementById('variant-popup-overlay')) {
      this.createVariantPopupHTML();
    }
    this.variantPopup.overlay = document.getElementById('variant-popup-overlay');
    const popup = this.variantPopup.overlay.querySelector('.variant-popup');
    const closeBtn = this.variantPopup.overlay.querySelector('.variant-popup-close');
    
    // Setup variant selector triggers for both patterns and books
    document.querySelectorAll('.variant-selector-trigger').forEach(label => {
      label.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        this.variantPopup.currentProductId = label.getAttribute('data-product-id');
        
        // Find checkbox by 'for' attribute, or fallback to finding by data-product-id
        let checkbox = document.getElementById(label.getAttribute('for'));
        
        // If checkbox not found, find it by data-product-id in the same container
        if (!checkbox) {
          const container = label.closest('.add-on-product-container, .add-on-element');
          if (container) {
            checkbox = container.querySelector(`input[data-product-id="${this.variantPopup.currentProductId}"]`);
          }
        }
        
        this.variantPopup.currentCheckbox = checkbox;
        
        // Determine type based on checkbox ID
        if (this.variantPopup.currentCheckbox) {
          if (this.variantPopup.currentCheckbox.id.startsWith('pattern-')) {
            this.variantPopup.currentType = 'pattern';
          } else if (this.variantPopup.currentCheckbox.id.startsWith('books-')) {
            this.variantPopup.currentType = 'books';
          }
        }
        
        // Handle mobile vs desktop
        if (this.isMobile) {
          this.toggleMobileVariantOptions(label);
        } else {
          this.openVariantPopup();
        }
      });
    });
    
    // Setup mobile variant option clicks
    this.setupMobileVariantOptions();
    
    // Close button (desktop)
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeVariantPopup());
    }
    
    // Click outside to close (desktop)
    if (this.variantPopup.overlay) {
      this.variantPopup.overlay.addEventListener('click', (e) => {
        if (e.target === this.variantPopup.overlay) {
          this.closeVariantPopup();
        }
      });
    }
    
    // Prevent popup from closing when clicking inside (desktop)
    if (popup) {
      popup.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
  }
  
  setupMobileVariantOptions() {
    // Setup click handlers for mobile variant options
    document.querySelectorAll('.variant-data .variant-option').forEach(option => {
      option.addEventListener('click', (e) => {
        if (!this.isMobile) return;
        
        const variantId = option.getAttribute('data-variant-id');
        const variantTitle = option.getAttribute('data-variant-title');
        const productId = option.closest('.variant-data').getAttribute('data-product-id');
        
        // Find the corresponding checkbox
        const container = document.querySelector(`[data-product-id="${productId}"]`).closest('.add-on-product-container, .add-on-element');
        const checkbox = container?.querySelector(`input[data-product-id="${productId}"]`);
        
        if (checkbox) {
          const isChecked = checkbox.checked;
          
          // Determine type
          if (checkbox.id.startsWith('pattern-')) {
            this.variantPopup.currentType = 'pattern';
          } else if (checkbox.id.startsWith('books-')) {
            this.variantPopup.currentType = 'books';
          }
          
          // Check capacity only if checkbox is not already checked
          if (!isChecked) {
            const currentWeight = this.calculateWeight();
            const remainingWeight = this.maxQty - currentWeight;
            const itemWeight = this.variantPopup.currentType === 'pattern' ? this.PATTERN_WEIGHT : this.BOOK_WEIGHT;
            
            if (remainingWeight < itemWeight) {
              this.showSoftMessage(
                `Add More Kits to Unlock Free Books & Patterns.`,
                container
              );
              return;
            }
          }
          
          this.variantPopup.currentCheckbox = checkbox;
          this.variantPopup.currentProductId = productId;
          
          this.selectVariant(variantId, variantTitle);
        }
        
        // Add selected class to clicked option, remove from others
        const variantData = option.closest('.variant-data');
        if (variantData) {
          variantData.querySelectorAll('.variant-option').forEach(opt => {
            opt.classList.remove('selected');
          });
          option.classList.add('selected');
        }
      });
    });
  }
  
  toggleMobileVariantOptions(label) {
    const productId = label.getAttribute('data-product-id');
    const variantData = document.querySelector(`.variant-data[data-product-id="${productId}"]`);
    
    if (!variantData) return;
    
    const isChecked = this.variantPopup.currentCheckbox.checked;
    const currentWeight = this.calculateWeight();
    const remainingWeight = this.maxQty - currentWeight;
    const itemWeight = this.variantPopup.currentType === 'pattern' ? this.PATTERN_WEIGHT : this.BOOK_WEIGHT;
    
    // If checkbox is not checked and there's no remaining capacity, show message
    if (!isChecked && remainingWeight < itemWeight) {
      const container = label.closest('.add-on-product-container, .add-on-element');
      this.showSoftMessage(
        `Add More Kits to Unlock Free Books & Patterns.`,
        container
      );
      return;
    }
    
    // Show current variant data (don't toggle, just add)
    variantData.classList.add('mobile-visible');
    
    // Highlight selected option if checkbox is checked
    if (isChecked) {
      const selectedVariantId = this.variantPopup.currentCheckbox.value;
      variantData.querySelectorAll('.variant-option').forEach(opt => {
        if (opt.getAttribute('data-variant-id') === selectedVariantId) {
          opt.classList.add('selected');
        } else {
          opt.classList.remove('selected');
        }
      });
    }
  }
  
  createVariantPopupHTML() {
    const popupHTML = `
      <div id="variant-popup-overlay" class="variant-popup-overlay" style="display: none;">
        <div class="variant-popup">
          <div class="variant-popup-header">
            <h3>Please choose your delivery method:</h3>
            <button class="variant-popup-close">&times;</button>
          </div>
          <div class="variant-popup-body">
            <div id="variant-options-list" class="variant-options-list_wrap"></div>
          </div>
        </div>
      </div>
    `;
    
    const targetElement = document.querySelector('.productView-product_right');
    if (targetElement) {
      targetElement.insertAdjacentHTML('beforeend', popupHTML);
    } else {
      document.body.insertAdjacentHTML('beforeend', popupHTML);
    }
  }
  
  openVariantPopup() {
    const optionsList = document.getElementById('variant-options-list');
    const variantData = document.querySelector(`.variant-data[data-product-id="${this.variantPopup.currentProductId}"]`);
    
    if (!variantData) return;
    const variants = variantData.querySelectorAll('.variant-option');
    const isChecked = this.variantPopup.currentCheckbox.checked;

    // Check if all items are selected (no remaining capacity)
    const currentWeight = this.calculateWeight();
    const remainingWeight = this.maxQty - currentWeight;
    
    // Determine the weight of the current item
    const itemWeight = this.variantPopup.currentType === 'pattern' ? this.PATTERN_WEIGHT : this.BOOK_WEIGHT;
    
    // If checkbox is not checked and there's no remaining capacity, show soft message
    if (!isChecked && remainingWeight < itemWeight) {
      const container = this.variantPopup.currentCheckbox.closest('.add-on-product-container, .add-on-element');
      this.showSoftMessage(
        `Add More Kits to Unlock Free Books & Patterns.`,
        container
      );
      return;
    }
    
    // Populate options
    optionsList.innerHTML = '';
    variants.forEach(variant => {
      const variantId = variant.getAttribute('data-variant-id');
      const variantTitle = variant.getAttribute('data-variant-title');
      const variantPrice = variant.getAttribute('data-variant-price');
      const variantAvailable = variant.getAttribute('data-variant-available');
      
      const optionItem = document.createElement('div');
      optionItem.className = 'variant-option-item';

      // Add disabled class if not available
      if (variantAvailable == 'false') {
        optionItem.classList.add('disabled');
      }
      
      if (isChecked && this.variantPopup.currentCheckbox.value === variantId) {
        optionItem.classList.add('selected');
      }
      optionItem.innerHTML = `
        <strong>${variantTitle}</strong>
      `;
      
      optionItem.addEventListener('click', () => {
        this.selectVariant(variantId, variantTitle);
      });
      
      optionsList.appendChild(optionItem);
    });
    
    // Show popup
    this.variantPopup.overlay.style.display = 'flex';
    setTimeout(() => this.variantPopup.overlay.classList.add('active'), 10);
  }
  
  selectVariant(variantId, variantTitle) {
    if (this.variantPopup.currentCheckbox) {
      const wasChecked = this.variantPopup.currentCheckbox.checked;
      const oldId = this.variantPopup.currentCheckbox.value;
      const type = this.variantPopup.currentType;
      const targetSet = type === 'pattern' ? this.selectedPatterns : this.selectedBooks;
      
      // Update selected set - remove old ID if it was checked
      if (wasChecked) {
        targetSet.delete(oldId);
      }
      
      // Update checkbox
      this.variantPopup.currentCheckbox.value = variantId;
      this.variantPopup.currentCheckbox.id = `${type}-${variantId}`;
      
      // Check the checkbox and add new variant to selected set
      this.variantPopup.currentCheckbox.checked = true;
      targetSet.add(variantId);
      
      // Update label for attribute
      const label = document.querySelector(`label[data-product-id="${this.variantPopup.currentProductId}"]`);
      if (label) {
        label.setAttribute('for', `${type}-${variantId}`);
      }
      
      // Update displayed variant name
      const variantNameDisplay = this.variantPopup.currentCheckbox.closest('.add-on-element').querySelector('.selected-variant-name');
      if (variantNameDisplay) {
        variantNameDisplay.textContent = variantTitle;
      }
      
      // Re-setup checkbox listener for the updated checkbox
      this.variantPopup.currentCheckbox.removeEventListener('change', this.handleCheckboxChange);
      this.variantPopup.currentCheckbox.addEventListener('change', (e) => this.handleCheckboxChange(e));
      
      // Update UI to reflect changes
      this.validateSelections();
      this.updateUI();
    }
    
    this.closeVariantPopup();
  }
  
  closeVariantPopup() {
    this.variantPopup.overlay.classList.remove('active');
    setTimeout(() => {
      this.variantPopup.overlay.style.display = 'none';
      this.variantPopup.currentProductId = null;
      this.variantPopup.currentCheckbox = null;
      this.variantPopup.currentType = null;
    }, 300);
  }
  
  handleCheckboxChange(e) {
    const checkbox = e.target;
    const isPattern = checkbox.id.startsWith('pattern-');
    const isBook = checkbox.id.startsWith('books-');
    const id = checkbox.value;
    const targetSet = isPattern ? this.selectedPatterns : this.selectedBooks;
    
    if (checkbox.checked) {
      const weight = isPattern ? this.PATTERN_WEIGHT : this.BOOK_WEIGHT;
      const currentWeight = this.calculateWeight();
      
      if (currentWeight + weight > this.maxQty) {
        checkbox.checked = false;
        this.showMessage(
          `Cannot add this item. Weight: ${weight}, Available: ${this.maxQty - currentWeight}`,
          'warning'
        );
        return;
      }
      targetSet.add(id);
    } else {
      targetSet.delete(id);
    }
    
    this.validateSelections();
    this.updateUI();
  }
  
  calculateWeight() {
    const patternWeight = this.selectedPatterns.size * this.PATTERN_WEIGHT;
    const bookWeight = this.selectedBooks.size * this.BOOK_WEIGHT;
    return patternWeight + bookWeight;
  }
  
  validateSelections() {
    const currentWeight = this.calculateWeight();
    
    if (currentWeight > this.maxQty) {
      const allSelected = [
        ...Array.from(this.selectedPatterns).map(id => ({ id, type: 'pattern', weight: this.PATTERN_WEIGHT })),
        ...Array.from(this.selectedBooks).map(id => ({ id, type: 'books', weight: this.BOOK_WEIGHT }))
      ];
      
      while (this.calculateWeight() > this.maxQty && allSelected.length > 0) {
        const lastItem = allSelected.pop();
        const checkbox = document.querySelector(`#${lastItem.type}-${lastItem.id}`);
        
        if (checkbox) checkbox.checked = false;
        if (lastItem.type === 'pattern') {
          this.selectedPatterns.delete(lastItem.id);
        } else {
          this.selectedBooks.delete(lastItem.id);
        }
      }
    }
  }
  
  updateUI() {
    this.updateDisplayArea();
    this.updateCheckboxStates();
    this.updateLabels();
  }
  
  updateDisplayArea() {
    const patternSpan = document.querySelector('.pattern-selected_options');
    const bookSpan = document.querySelector('.books-selected_options');
    
    if (patternSpan) {
      patternSpan.innerHTML = this.createDisplayHTML(this.selectedPatterns, 'pattern');
    }
    if (bookSpan) {
      bookSpan.innerHTML = this.createDisplayHTML(this.selectedBooks, 'books');
    }
  }
  
  createDisplayHTML(selectedSet, type) {
    let html = '';
    selectedSet.forEach(id => {
      const checkbox = document.querySelector(`#${type}-${id}`);
      if (checkbox) {
        const patternElement = checkbox.nextElementSibling?.querySelector('.pattern');
        const label = patternElement?.textContent || 'Selected';
        
        const tempDiv = document.createElement('div');
        tempDiv.style.cssText = patternElement?.getAttribute('style') || '';
        const bgImage = window.getComputedStyle(tempDiv).backgroundImage;
        
        const tagId = `tag-${type}-${id}`;
        html += `<span class="selected-tag" id="${tagId}" data-id="${id}" data-type="${type}" title="${label}">
                  <span class="selected-tag-image"></span>
                  <button type="button" class="remove-tag" data-id="${id}" data-type="${type}" aria-label="Remove selection">×</button>
                </span>`;
      }
    });
    
    setTimeout(() => {
      selectedSet.forEach(id => {
        const checkbox = document.querySelector(`#${type}-${id}`);
        if (checkbox) {
          const patternElement = checkbox.nextElementSibling?.querySelector('.pattern');
          const bgColor = patternElement?.style.backgroundColor || '#ccc';
          const bgImage = patternElement?.style.backgroundImage || '';
          
          const tagImage = document.querySelector(`#tag-${type}-${id} .selected-tag-image`);
          if (tagImage) {
            if (bgImage) {
              tagImage.style.backgroundImage = bgImage;
            }
            tagImage.style.backgroundColor = bgColor;
          }
        }
      });
      
      document.querySelectorAll('.remove-tag').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const id = btn.dataset.id;
          const typeVal = btn.dataset.type;
          const checkbox = document.querySelector(`#${typeVal}-${id}`);
          if (checkbox) {
            checkbox.checked = false;
            checkbox.dispatchEvent(new Event('change'));
            
            // Remove selected class from variant options
            const productId = checkbox.getAttribute('data-product-id');
            if (productId) {
              const variantData = document.querySelector(`.variant-data[data-product-id="${productId}"]`);
              if (variantData) {
                variantData.querySelectorAll('.variant-option').forEach(opt => {
                  opt.classList.remove('selected');
                });
              }
            }
          }
        });
      });
    }, 0);
    
    return html;
  }
  
  updateCheckboxStates() {
    const currentWeight = this.calculateWeight();
    const remainingWeight = this.maxQty - currentWeight;
    
    document.querySelectorAll('input[type="checkbox"][name="additonal-id"]').forEach(checkbox => {
      const isSelected = checkbox.checked;
      const isPattern = checkbox.id.startsWith('pattern-');
      const itemWeight = isPattern ? this.PATTERN_WEIGHT : this.BOOK_WEIGHT;
      const shouldDisable = !isSelected && itemWeight > remainingWeight;
      
      if (shouldDisable) {
        checkbox.disabled = true;
        checkbox.parentElement.classList.add('disabled');
      } else {
        checkbox.disabled = false;
        checkbox.parentElement.classList.remove('disabled');
      }
    });
  }
  
  updateLabels() {
    const patternLabel = document.querySelector('.free-pattern_label strong');
    const bookLabel = document.querySelector('.free-book_label strong');
    
    if (patternLabel) {
      const patternsRemaining = this.maxQty - this.calculateWeight();
      if (patternsRemaining > 0) {
        const patternWord = patternsRemaining === 1 ? 'Pattern' : 'Patterns';
        patternLabel.textContent = `You've Earned ${patternsRemaining} Free ${patternWord} - select below`;
      } else {
        patternLabel.textContent = `All free patterns selected`;
      }
    }
    
    if (bookLabel) {
      const currentQty = parseInt(document.querySelector('.quantity__input')?.value || 0);
      const totalKits = currentQty + this.withoutDiscountKits;
      
      const patternsRemaining = this.maxQty - this.calculateWeight();
      const remainingKits = patternsRemaining;
      
      const freeBooks = Math.floor(remainingKits / 2);
      
      const selectedBooks = document.querySelectorAll('.free-book_item input[type="checkbox"]:checked').length;
      const booksRemaining = freeBooks - selectedBooks;
      
      if (freeBooks >= 1) {
        const bookWord = booksRemaining === 1 ? 'Book' : 'Books';
        
        if (booksRemaining > 0) {
          bookLabel.textContent = `You've Earned ${booksRemaining} Free ${bookWord} - choose your free book`;
        } else {
          bookLabel.textContent = `All free books selected`;
        }
      } else {
        const moreNeeded = 2 - remainingKits;
        
        if (remainingKits > 0) {
          const patternWord = moreNeeded === 1 ? 'kit' : 'kits';
          bookLabel.textContent = `Add ${moreNeeded} more ${patternWord} to unlock the Book Deal`;
        } else {
          bookLabel.textContent = `Add more kits to unlock the Book Deal`;
        }
      }
    }
  }
  
  showMessage(text, type = 'info') {
    const msg = document.createElement('div');
    msg.className = `temp-message ${type}`;
    msg.textContent = text;
    document.body.appendChild(msg);
    setTimeout(() => msg.remove(), 3000);
  }
  
  showSoftMessage(text, container) {
    // Remove any existing soft message
    const existingMsg = document.querySelector('.soft-message');
    if (existingMsg) {
      existingMsg.remove();
    }
    
    // Find the .product-pattern-options_wrap element - search from container or document
    let optionsWrap = container?.querySelector('.product-pattern-options_wrap');
    
    // If not found in container, search in document
    if (!optionsWrap) {
      optionsWrap = document.querySelector('.product-pattern-options_wrap');
    }
    
    if (!optionsWrap) {
      console.warn('.product-pattern-options_wrap not found');
      return;
    }
    
    // Create soft message
    const msg = document.createElement('div');
    msg.className = 'soft-message';
    msg.textContent = text;
    
    // Prepend as first child of .product-pattern-options_wrap
    optionsWrap.insertBefore(msg, optionsWrap.firstChild);
    
    // Auto remove after 4 seconds
    setTimeout(() => msg.remove(), 4000);
  }
  
}

document.addEventListener('DOMContentLoaded', () => {
  window.selectionManager = new SelectionManager();
});

/**
 * Global function: Clears all selections within a specified parent element
 * Can be called from anywhere: clearSelectionsByParent('.product-pattern-options')
 * @param {string} parentSelector - CSS selector for the parent element
 */
function clearSelectionsByParent(parentSelector) {
  const parentElement = document.querySelector(parentSelector);
  
  if (!parentElement) {
    console.warn(`Parent element "${parentSelector}" not found`);
    return;
  }
  
  // Find all checkboxes within the parent
  const checkboxes = parentElement.querySelectorAll('input[type="checkbox"][name="additonal-id"]');
  
  // Track which IDs to remove from the manager
  const idsToRemove = {
    patterns: [],
    books: []
  };
  
  // Uncheck all checkboxes and collect IDs
  checkboxes.forEach(checkbox => {
    if (checkbox.checked) {
      const isPattern = checkbox.id.startsWith('pattern-');
      const isBook = checkbox.id.startsWith('books-');
      const id = checkbox.value;
      
      // Collect IDs to remove
      if (isPattern) {
        idsToRemove.patterns.push(id);
      } else if (isBook) {
        idsToRemove.books.push(id);
      }
      
      // Uncheck the checkbox
      checkbox.checked = false;
    }
  });
  
  // Remove IDs from SelectionManager if it exists
  if (window.selectionManager) {
    idsToRemove.patterns.forEach(id => {
      window.selectionManager.selectedPatterns.delete(id);
    });
    idsToRemove.books.forEach(id => {
      window.selectionManager.selectedBooks.delete(id);
    });
  }
  
  // Clear the display areas within the parent
  const patternDisplay = parentElement.querySelector('.pattern-selected_options');
  const bookDisplay = parentElement.querySelector('.books-selected_options');
  
  if (patternDisplay) {
    patternDisplay.innerHTML = '';
  }
  if (bookDisplay) {
    bookDisplay.innerHTML = '';
  }

  // Reset quantity input to 1
  const quantityInput = document.querySelector('input.form-input.quantity__input');
  if (quantityInput) {
    quantityInput.value = 1;
    // Trigger change event to update any listeners
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
  
  // Update UI if SelectionManager exists
  if (window.selectionManager) {
    window.selectionManager.validateSelections();
    window.selectionManager.updateUI();
  }
}
